import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { readFile } from '../../utils/fileLoader';

const RangeContext = createContext();

// Maintain existing VALID_BET_TYPES
export const VALID_BET_TYPES = {
  OPEN: 'OPEN',
  FACING_OPEN_CALL: 'FACING_OPEN_CALL',
  FACING_OPEN_3BET: 'FACING_OPEN_3BET',
  FACING_3BET_CALL: 'FACING_3BET_CALL',
  FACING_3BET_4BET: 'FACING_3BET_4BET',
  FACING_4BET_CALL: 'FACING_4BET_CALL',
  SQUEEZE_4BET: 'SQUEEZE_4BET',
  CALL_VS_3BET: 'CALL_VS_3BET'
};

// Export getFilePath here to avoid duplication
export const getFilePath = (config) => {
  if (!config?.position || !config?.betType) {
    throw new Error('Position and betType are required in config');
  }

  const pos = config.position.toLowerCase();
  const betType = config.betType.toUpperCase();
  const vsPos = config.openPosition?.toLowerCase();
  const threeBetPos = config.threeBetPosition?.toLowerCase();
  const baseFolder = '/database/preflop/ranges';

  console.log('Processing range path config:', {
    pos,
    betType,
    vsPos,
    threeBetPos,
    originalConfig: config
  });


  // Map bet types to file paths with correct naming conventions
  switch (betType) {
    case 'OPEN':
    case VALID_BET_TYPES.OPEN:
      return `${baseFolder}/${pos}/${pos}_open.csv`;
      
    case '3BET':
    case VALID_BET_TYPES.FACING_OPEN_3BET:
      return `${baseFolder}/${pos}/${pos}_3bet-${vsPos}_open.csv`;
      
    case '4BET':
    case VALID_BET_TYPES.FACING_3BET_4BET: {
      // Use the correct format for 4bet ranges
      const targetPos = threeBetPos || vsPos || 'bb';
      return `${baseFolder}/${pos}/${pos}_4bet-${targetPos}_3bet.csv`;
    }
      
    case 'CALL':
    case VALID_BET_TYPES.FACING_OPEN_CALL:
      if (!vsPos) {
        console.warn('No openPosition provided for call, using default');
        return `${baseFolder}/${pos}/${pos}_open.csv`; // fallback
      }
      return `${baseFolder}/${pos}/${pos}_call-${vsPos}_open.csv`;
      
    case VALID_BET_TYPES.FACING_3BET_CALL:
    case 'CALL_VS_3BET': {
      // For call vs 3bet, prioritize threeBetPosition if available
      const threePos = threeBetPos || vsPos || 'bb';
      if (!threePos) {
        console.warn('No position provided for call vs 3bet, using default');
        return `${baseFolder}/${pos}/${pos}_open.csv`; // fallback
      }
      return `${baseFolder}/${pos}/${pos}_call-${threePos}_3bet.csv`;
    }
      
    case VALID_BET_TYPES.SQUEEZE_4BET:
    case 'SQUEEZE_4BET':
    case 'SQUEEZE':
      // Handle squeeze scenarios
      if (!vsPos || !threeBetPos) {
        console.warn('Missing positions for squeeze, using default');
        return `${baseFolder}/${pos}/${pos}_open.csv`; // fallback
      }
      return `${baseFolder}/${pos}/${pos}_squeeze-${vsPos}_open-${threeBetPos}_3bet.csv`;
      
      default:
        throw new Error(`Unknown bet type: ${betType}`);
    }
  };

// Enhanced range cache with expiration (24 hours)
const rangeCache = new Map();
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Minimal fallback for when ranges can't be loaded
const getDefaultRange = () => {
  const defaultRange = new Map();
  // Add top premium hands (extremely tight basic range)
  defaultRange.set('AA', 100);
  defaultRange.set('KK', 100);
  defaultRange.set('QQ', 100);
  defaultRange.set('AKs', 100);
  return defaultRange;
};

// Store loaded ranges in localStorage for offline support
const saveRangeToStorage = (key, data) => {
  try {
    const item = {
      hands: Array.from(data.hands.entries()),
      title: data.title,
      timestamp: data.timestamp
    };
    localStorage.setItem(`range_${key}`, JSON.stringify(item));
  } catch (error) {
    console.warn('Failed to save range to localStorage:', error);
  }
};

// Load range from localStorage
const loadRangeFromStorage = (key) => {
  try {
    const data = localStorage.getItem(`range_${key}`);
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    return {
      hands: new Map(parsed.hands),
      title: parsed.title,
      timestamp: parsed.timestamp
    };
  } catch (error) {
    console.warn('Failed to load range from localStorage:', error);
    return null;
  }
};

// Parse CSV data — captures action column for blast ranges.
// Returns Map<key, { type, actions: { raise?, allin?, call?, open?, ... } }>
// For CSVs without an action column, all frequency goes under 'open'.
// Multiple rows for the same hand (mixed strategy) are aggregated.
function parseRangeFromCSV(csvData) {
  const rangeMap = new Map();

  csvData.forEach(row => {
    const hand      = row.hand;
    const type      = row.type || '';
    const frequency = parseInt(row.frequency) || 0;
    const action    = row.action?.toLowerCase() || 'open';

    if (!hand || frequency <= 0) return;

    let key = hand;
    if (type === 'suited' && !hand.endsWith('s'))  key = `${hand}s`;
    else if (type === 'offsuit' && !hand.endsWith('o')) key = `${hand}o`;

    const existing = rangeMap.get(key) || { type, actions: {} };
    existing.actions[action] = (existing.actions[action] || 0) + frequency;
    rangeMap.set(key, existing);
  });

  return rangeMap;
}

export const RangeProvider = ({ children }) => {
  const [rangeConfig, setRangeConfig] = useState(null);
  const [activeRange, setActiveRange] = useState(new Map());
  const [rangeTitle, setRangeTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  const [isFallback, setIsFallback] = useState(false);
  const loadingPromiseRef = useRef(null);

  // Add network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    // Check initial network status
    setIsOffline(!navigator.onLine);
    
    // Add event listeners for network status changes
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getCacheKey = (position, betType, rangePath) => {
    return `${position}_${betType}_${rangePath}`;
  };

  // Clean expired cache entries periodically
  useEffect(() => {
    const cleanCache = () => {
      const now = Date.now();
      for (const [key, value] of rangeCache.entries()) {
        if (now - value.timestamp > CACHE_EXPIRATION) {
          rangeCache.delete(key);
        }
      }
    };
    
    // Clean cache on mount and every hour
    cleanCache();
    const interval = setInterval(cleanCache, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadRange = async ({ position, betType, rangePath }) => {
    try {
      console.log('Loading range with params:', { position, betType, rangePath });
      setError(null);
      setIsFallback(false);
  
      // Create cache key
      const cacheKey = getCacheKey(position, betType, rangePath);
  
      // If this exact range is currently loading, wait for it
      if (loadingPromiseRef.current) {
        console.log('Range already loading, waiting for completion');
        return loadingPromiseRef.current;
      }
  
      // Check memory cache first
      if (rangeCache.has(cacheKey)) {
        console.log('Range found in memory cache');
        const cachedData = rangeCache.get(cacheKey);
        
        // Check if cache has expired
        if (Date.now() - cachedData.timestamp < CACHE_EXPIRATION) {
          setActiveRange(cachedData.hands);
          setRangeTitle(cachedData.title);
          setRangeConfig({ position, betType, rangePath });
          return cachedData.hands;
        } else {
          console.log('Cache expired, removing');
          rangeCache.delete(cacheKey);
        }
      }
  
      setIsLoading(true);
  
      // Create new loading promise
      loadingPromiseRef.current = (async () => {
        try {
          let rangeData = null;
  
          // If offline, try loading from localStorage
          if (isOffline) {
            console.log('Offline mode: trying to load from localStorage');
            rangeData = loadRangeFromStorage(cacheKey);
            
            if (rangeData) {
              console.log('Range loaded from localStorage');
            } else {
              console.log('No cached range in localStorage, using minimal emergency fallback');
              // Use minimal emergency fallback when offline
              const defaultHands = getDefaultRange();
              const title = `Emergency Fallback Range (Offline)`;
              setIsFallback(true);
              rangeData = {
                hands: defaultHands,
                title,
                timestamp: Date.now()
              };
            }
          } else {
            // Online mode: use fetch directly
            const normalizedPath = rangePath.startsWith('/') ? rangePath.substring(1) : rangePath;
            const fetchUrl = `/${normalizedPath}`;
            
            console.log('Fetching range from:', fetchUrl);
            
            try {
              // Fetch with a per-attempt timeout, retried up to twice before
              // falling back — same reasoning as fetchWithRetry (src/utils/api.js):
              // a transient network blip or Vercel cold response shouldn't
              // permanently land on "range data not available for this
              // scenario," which previously fired on the very first fetch
              // attempt at any hiccup, especially likely right after a fresh
              // TestFlight install when there's no localStorage cache yet to
              // quietly cover for it. 5s -> 8s per attempt, plus 2 retries.
              let response;
              let lastFetchError;
              for (let attempt = 0; attempt <= 2; attempt++) {
                try {
                  const controller = new AbortController();
                  const timeoutId = setTimeout(() => controller.abort(), 8000);
                  response = await fetch(fetchUrl, {
                    signal: controller.signal,
                    headers: { 'Cache-Control': 'no-cache' }
                  });
                  clearTimeout(timeoutId);
                  if (response.ok) break;
                  lastFetchError = new Error(`Failed to load range file: ${response.status} ${response.statusText}`);
                } catch (fetchErr) {
                  lastFetchError = fetchErr;
                  response = null;
                }
                if (attempt < 2) {
                  console.log(`Range fetch attempt ${attempt + 1} failed, retrying:`, lastFetchError?.message);
                  await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
                }
              }

              if (!response || !response.ok) {
                throw lastFetchError || new Error('Failed to load range file');
              }

              const text = await response.text();
                console.log('Range data loaded, size:', text.length);

                if (!text || text.length < 50) {
                  throw new Error('Range file is empty or too small');
                }

                // Parse CSV data into lines
                const lines = text.split('\n').map(line => line.trim()).filter(Boolean);

                if (lines.length < 2) {
                  throw new Error('Range file has insufficient data');
                }

                // Extract headers from first line
                const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
                const handIndex   = headers.indexOf('hand');
                const typeIndex   = headers.indexOf('type');
                const freqIndex   = headers.indexOf('frequency');
                const actionIndex = headers.indexOf('action');

                if (handIndex === -1 || freqIndex === -1) {
                  throw new Error(`Invalid range file format: missing required headers`);
                }

                // Convert CSV rows into objects (including action column if present)
                const csvData = [];
                for (let i = 1; i < lines.length; i++) {
                  const parts = lines[i].split(',').map(p => p.trim());
                  if (parts.length <= Math.max(handIndex, freqIndex)) continue;
                  csvData.push({
                    hand:      parts[handIndex],
                    type:      typeIndex   !== -1 ? parts[typeIndex].toLowerCase()  : '',
                    frequency: parts[freqIndex],
                    action:    actionIndex !== -1 ? parts[actionIndex]?.toLowerCase() : undefined,
                  });
                }

                // parseRangeFromCSV aggregates multiple rows per hand and captures action data.
                // Returns Map<key, { type, actions: { raise?, allin?, call?, open?, ... } }>
                const newHands = parseRangeFromCSV(csvData);

                if (newHands.size === 0) {
                  console.warn('No valid hands found in range file');
                  throw new Error('No valid hands found in range file');
                }

                const title = `${position} ${betType} Range`;
                console.log('Setting range title:', title, 'with', newHands.size, 'hands');

              rangeData = {
                hands: newHands,
                title,
                timestamp: Date.now()
              };
              
              // Save to localStorage for offline use
              saveRangeToStorage(cacheKey, rangeData);
            } catch (error) {
              console.error('Error fetching range:', error);
              
              // Try localStorage as backup if fetch fails
              console.log('Trying localStorage as backup');
              rangeData = loadRangeFromStorage(cacheKey);
              
              if (!rangeData) {
                // If no localStorage backup, use minimal emergency fallback
                console.log('No backup in localStorage, using minimal emergency fallback');
                const defaultHands = getDefaultRange();
                setIsFallback(true);
                rangeData = {
                  hands: defaultHands,
                  title: `Emergency Fallback Range`,
                  timestamp: Date.now()
                };
              } else {
                console.log('Using cached range from localStorage');
              }
            }
          }
  
          // Cache the results in memory
          rangeCache.set(cacheKey, rangeData);
          setRangeTitle(rangeData.title);
          setActiveRange(rangeData.hands);
          setRangeConfig({ position, betType, rangePath });
  
          console.log('Range loaded successfully with', rangeData.hands.size, 'hands');
          return rangeData.hands;
  
        } catch (error) {
          console.error('Error processing range:', error);
          setError(error.message);

          // Return minimal emergency fallback on failure
          const defaultRange = getDefaultRange();
          setIsFallback(true);
          setActiveRange(defaultRange);
          setRangeTitle(`Emergency Fallback Range (Error)`);
          return defaultRange;
        }
      })();

      const result = await loadingPromiseRef.current;
      return result;

    } catch (error) {
      console.error('Error in loadRange:', error);
      setError(error.message);

      // Return minimal emergency fallback on failure
      const defaultRange = getDefaultRange();
      setIsFallback(true);
      setActiveRange(defaultRange);
      setRangeTitle(`Emergency Fallback Range (Error)`);
      return defaultRange;
    } finally {
      setIsLoading(false);
      loadingPromiseRef.current = null;
    }
  };

  // Retry loading on network reconnection
  useEffect(() => {
    if (!isOffline && rangeConfig && error) {
      console.log('Network reconnected, retrying range load');
      loadRange(rangeConfig).catch(e => console.error('Retry failed:', e));
    }
  }, [isOffline, rangeConfig, error]);

  return (
    <RangeContext.Provider value={{
      rangeConfig,
      activeRange,
      rangeTitle,
      loadRange,
      isLoading,
      error,
      isOffline,
      isFallback,
    }}>
      {children}
    </RangeContext.Provider>
  );
};

export const useRange = () => {
  const context = useContext(RangeContext);
  if (!context) {
    throw new Error('useRange must be used within a RangeProvider');
  }
  return context;
};

export { RangeContext };